import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const EMPTY = { business_name: "", owner_name: "", phone: "", email: "", notes: "" };

export default function Businesses() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/businesses", { params: { search: search || undefined } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/businesses", form);
      toast.success("Business created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="businesses-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Businesses</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-business-button">New Business</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Business</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[
                ["business_name", "Business Name *"],
                ["owner_name", "Owner Name *"],
                ["phone", "Phone *"],
                ["email", "Email (optional)"],
              ].map(([k, l]) => (
                <div key={k} className="space-y-1.5">
                  <Label>{l}</Label>
                  <Input
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`business-${k}-input`}
                  />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label>Notes (optional)</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="business-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} data-testid="save-business-button">
                {saving ? "Saving..." : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2 mb-4">
        <Input
          placeholder="Search name, owner, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="max-w-xs"
          data-testid="business-search-input"
        />
        <Button variant="outline" onClick={load} data-testid="business-search-button">Search</Button>
      </div>

      <div className="bg-white border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Cards</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((b) => (
              <TableRow
                key={b.id}
                className="cursor-pointer"
                onClick={() => navigate(`/businesses/${b.id}`)}
                data-testid={`business-row-${b.id}`}
              >
                <TableCell className="font-medium">{b.business_name}</TableCell>
                <TableCell>{b.owner_name}</TableCell>
                <TableCell>{b.phone}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell>{b.card_count}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-8">No businesses yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
